import { useMemo, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import {
  ArrowDown,
  ArrowUp,
  ChevronRight,
  Clock3,
  HelpCircle,
  Loader2,
  Search,
  Trophy,
  Users,
} from 'lucide-react';
import {
  Line,
  LineChart,
  ResponsiveContainer,
  YAxis,
} from 'recharts';
import AdminLayout from '../../components/admin/AdminLayout';
import { DEMO_TEACHER_BASE } from '../../lib/demo-teacher';
import { Input } from '../../components/ui/input';
import {
  getPublicRankingSample,
  getTeacherRanking,
  type TeacherRankingStudent,
} from '../../lib/admin-api';
import { cn } from '../../lib/utils';

type SortKey = 'rank' | 'name' | 'participantCode' | 'interviewCount' | 'totalUsageSeconds' | 'averageScore';
type SortDirection = 'asc' | 'desc';

type RankedStudent = TeacherRankingStudent & { rank: number };

const stringCollator = new Intl.Collator('ja', { numeric: true, sensitivity: 'base' });

function defaultSortDirection(key: SortKey): SortDirection {
  return key === 'interviewCount' || key === 'totalUsageSeconds' || key === 'averageScore'
    ? 'desc'
    : 'asc';
}

const PERIODS = [
  { value: 7, label: '直近7日' },
  { value: 30, label: '直近30日' },
  { value: 90, label: '直近90日' },
  { value: 0, label: '全期間' },
];

function formatDuration(value: number): string {
  const seconds = Math.max(0, Math.round(value));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  return `${hours}:${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
}

function scoreTone(score: number): string {
  if (score >= 80) return 'bg-emerald-100 text-emerald-900';
  if (score >= 50) return 'bg-lime-100 text-lime-900';
  if (score > 0) return 'bg-amber-100 text-amber-900';
  return 'bg-red-100 text-red-900';
}

export default function TeacherRanking() {
  return <RankingPage />;
}

export function PublicRankingSample() {
  return <RankingPage publicSample />;
}

/** 教員画面の公開デモ（/demo/teacher/rankings）。公開サンプルのデータを教員メニューの中で表示する */
export function DemoTeacherRanking() {
  return <RankingPage publicSample demoLayout />;
}

function RankingPage({ publicSample = false, demoLayout = false }: { publicSample?: boolean; demoLayout?: boolean }) {
  const [days, setDays] = useState(publicSample ? 0 : 90);
  const [grade, setGrade] = useState<number | null>(null);
  const [classId, setClassId] = useState<number | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('rank');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [search, setSearch] = useState('');
  const { data, isLoading, error } = useQuery({
    queryKey: publicSample
      ? ['public', 'ranking-sample', days, grade, classId]
      : ['teacher', 'ranking', days, grade, classId],
    queryFn: () =>
      publicSample
        ? getPublicRankingSample(days, classId, grade)
        : getTeacherRanking(days, classId, grade),
    staleTime: 60 * 1000,
  });

  const students = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    const scoreOrder = [...(data?.students ?? [])].sort((a, b) => {
      const scoreDiff = b.averageScore - a.averageScore;
      if (scoreDiff !== 0) return scoreDiff;
      const interviewDiff = b.interviewCount - a.interviewCount;
      if (interviewDiff !== 0) return interviewDiff;
      return stringCollator.compare(a.name, b.name);
    });
    const rankByStudentId = new Map(scoreOrder.map((student, index) => [student.studentId, index + 1]));

    return (data?.students ?? [])
      .map<RankedStudent>((student) => ({
        ...student,
        rank: rankByStudentId.get(student.studentId) ?? 0,
      }))
      .filter((student) => {
        if (!keyword) return true;
        return [student.name, student.participantCode, student.groupLabel]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(keyword));
      })
      .sort((a, b) => {
        let comparison = 0;
        switch (sortKey) {
          case 'rank':
            comparison = a.rank - b.rank;
            break;
          case 'name':
            comparison = stringCollator.compare(a.name, b.name);
            break;
          case 'participantCode': {
            const aCode = a.participantCode?.trim();
            const bCode = b.participantCode?.trim();
            if (!aCode && !bCode) comparison = 0;
            else if (!aCode) return 1;
            else if (!bCode) return -1;
            else comparison = stringCollator.compare(aCode, bCode);
            break;
          }
          case 'interviewCount':
            comparison = a.interviewCount - b.interviewCount;
            break;
          case 'totalUsageSeconds':
            comparison = a.totalUsageSeconds - b.totalUsageSeconds;
            break;
          case 'averageScore':
            comparison = a.averageScore - b.averageScore;
            break;
        }
        if (comparison !== 0) return sortDirection === 'asc' ? comparison : -comparison;
        return a.rank - b.rank;
      });
  }, [data?.students, search, sortDirection, sortKey]);

  const changeSort = (nextKey: SortKey) => {
    if (nextKey === sortKey) {
      setSortDirection((current) => current === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortKey(nextKey);
    setSortDirection(defaultSortDirection(nextKey));
  };

  const content = (
      <div className="space-y-6">
        <header>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900">ランキング</h1>
            {publicSample && (
              <span className="rounded-full bg-cyan-100 px-3 py-1 text-xs font-bold text-cyan-900">
                公開サンプル
              </span>
            )}
          </div>
          <p className="mt-2 text-sm text-gray-600">
            {publicSample
              ? "簡易ログイン番号100〜400の利用状況と平均スコアを匿名表示しています。"
              : "担当学生の利用状況と平均スコアを比較します。"}
          </p>
        </header>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
            対象期間
            <select
              value={days}
              onChange={(event) => setDays(Number(event.target.value))}
              className="h-11 rounded-lg border border-gray-300 bg-white px-3 text-sm font-normal text-gray-900"
            >
              {PERIODS.map((period) => (
                <option key={period.value} value={period.value}>{period.label}</option>
              ))}
            </select>
          </label>

          <>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                学年
                <select
                  value={grade ?? ''}
                  onChange={(event) => {
                    setGrade(event.target.value ? Number(event.target.value) : null);
                    setClassId(null);
                  }}
                  className="h-11 rounded-lg border border-gray-300 bg-white px-3 text-sm font-normal text-gray-900"
                  aria-label="対象学年"
                >
                  <option value="">全学年</option>
                  <option value="1">1年生</option>
                  <option value="2">2年生</option>
                  <option value="3">3年生</option>
                </select>
              </label>

              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                クラス
                <select
                  value={classId ?? ''}
                  onChange={(event) => setClassId(event.target.value ? Number(event.target.value) : null)}
                  className="h-11 rounded-lg border border-gray-300 bg-white px-3 text-sm font-normal text-gray-900"
                  aria-label="対象クラス"
                >
                  <option value="">全担当学生</option>
                  {(data?.classes ?? [])
                    .filter((educatorClass) => grade == null || educatorClass.grade === grade)
                    .map((educatorClass) => (
                    <option key={educatorClass.id} value={educatorClass.id}>
                      {educatorClass.name}（{educatorClass.studentCount}名）
                    </option>
                    ))}
                </select>
              </label>
          </>

          <div className="relative min-w-0 flex-1 lg:max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="学生名・学籍番号を検索"
              className="h-11 pl-10"
            />
          </div>

        </div>

        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin" />
            ランキングを集計しています...
          </div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-5 text-red-700">
            ランキングを読み込めませんでした。
          </div>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <SummaryMetric label="アクティブ学生数" value={`${data?.summary.activeStudentCount ?? 0}名`} icon={<Users />} />
              <SummaryMetric label="ロールプレイ総数" value={`${data?.summary.totalInterviewCount ?? 0}回`} icon={<Trophy />} />
              <SummaryMetric
                label="平均利用時間"
                value={formatDuration(data?.summary.averageUsageSeconds ?? 0)}
                icon={<Clock3 />}
                help="面接内の最初と最後の会話記録時刻から算出した推定値です。"
              />
              <SummaryMetric label="平均スコア" value={`${Number(data?.summary.averageScore ?? 0).toFixed(1)}点`} icon={<Trophy />} />
            </div>

            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
              <div className="overflow-x-auto">
                <table className={`w-full border-collapse text-left ${publicSample ? 'min-w-[980px]' : 'min-w-[1060px]'}`}>
                  <thead className="bg-gray-50 text-sm text-gray-600">
                    <tr>
                      <SortableHeader label="順位" sortKey="rank" activeKey={sortKey} direction={sortDirection} onSort={changeSort} className="w-24" />
                      <SortableHeader label="氏名" sortKey="name" activeKey={sortKey} direction={sortDirection} onSort={changeSort} />
                      <SortableHeader label="学籍番号" sortKey="participantCode" activeKey={sortKey} direction={sortDirection} onSort={changeSort} />
                      <SortableHeader label="ロールプレイ回数" sortKey="interviewCount" activeKey={sortKey} direction={sortDirection} onSort={changeSort} />
                      <SortableHeader
                        label="総利用時間"
                        sortKey="totalUsageSeconds"
                        activeKey={sortKey}
                        direction={sortDirection}
                        onSort={changeSort}
                        help="最初と最後の会話記録時刻から算出"
                      />
                      <SortableHeader label="平均スコア" sortKey="averageScore" activeKey={sortKey} direction={sortDirection} onSort={changeSort} />
                      <th className="w-44 px-4 py-3 font-bold">スコア推移</th>
                      <th className="w-12 px-2 py-3"><span className="sr-only">学生詳細</span></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {students.map((student) => (
                      <RankingRow key={student.studentId} student={student} publicSample={publicSample} demoLayout={demoLayout} />
                    ))}
                  </tbody>
                </table>
              </div>
              {students.length === 0 && (
                <div className="px-6 py-12 text-center text-sm text-gray-500">対象の学生はいません。</div>
              )}
            </div>

            <p className="text-xs text-gray-500">
              推定利用時間は暫定値です。正確な操作時間は、面接開始・終了時刻の記録追加後に置き換えます。
            </p>
          </>
        )}
      </div>
  );

  if (!publicSample) return <AdminLayout>{content}</AdminLayout>;
  if (demoLayout) return <AdminLayout demo>{content}</AdminLayout>;
  return (
    <div className="min-h-screen bg-gray-50 text-gray-950">
      <div className="border-b border-cyan-900 bg-cyan-950 px-5 py-4 text-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
          <div>
            <div className="text-lg font-bold">Dental Avatar</div>
            <div className="text-xs font-medium text-cyan-100">教員ダッシュボード体験</div>
          </div>
          <span className="rounded-full border border-cyan-600 px-3 py-1 text-xs font-bold text-cyan-50">
            閲覧専用
          </span>
        </div>
      </div>
      <main className="mx-auto max-w-7xl px-4 py-7 sm:px-6 lg:px-8">
        {content}
      </main>
    </div>
  );
}

function SortableHeader({
  label,
  sortKey,
  activeKey,
  direction,
  onSort,
  help,
  className,
}: {
  label: string;
  sortKey: SortKey;
  activeKey: SortKey;
  direction: SortDirection;
  onSort: (key: SortKey) => void;
  help?: string;
  className?: string;
}) {
  const active = sortKey === activeKey;
  const nextDirection = active
    ? (direction === 'asc' ? 'desc' : 'asc')
    : defaultSortDirection(sortKey);
  return (
    <th
      className={cn('px-4 py-2 font-bold', className)}
      aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="flex min-h-9 w-full items-center gap-1.5 rounded-md px-1 text-left hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-cyan-700"
        aria-label={`${label}を${nextDirection === 'asc' ? '昇順' : '降順'}に並べ替え`}
      >
        <span>{label}</span>
        {help && (
          <span title={help} aria-label={help}>
            <HelpCircle className="h-4 w-4 text-gray-400" />
          </span>
        )}
        <span className="ml-auto inline-flex items-center" aria-hidden="true">
          <ArrowUp className={cn('h-3.5 w-3.5', active && direction === 'asc' ? 'text-cyan-800' : 'text-gray-300')} />
          <ArrowDown className={cn('-ml-1 h-3.5 w-3.5', active && direction === 'desc' ? 'text-cyan-800' : 'text-gray-300')} />
        </span>
      </button>
    </th>
  );
}

function SummaryMetric({
  label,
  value,
  icon,
  help,
}: {
  label: string;
  value: string;
  icon: ReactElement;
  help?: string;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex items-center justify-between gap-3 text-sm font-bold text-gray-600">
        <span className="inline-flex items-center gap-1">
          {label}
          {help && (
            <span title={help} aria-label={help}>
              <HelpCircle className="h-4 w-4 text-gray-400" />
            </span>
          )}
        </span>
        <span className="text-cyan-800 [&>svg]:h-5 [&>svg]:w-5">{icon}</span>
      </div>
      <div className="mt-3 text-2xl font-bold text-gray-900">{value}</div>
    </div>
  );
}

function RankingRow({
  student,
  publicSample,
  demoLayout = false,
}: {
  student: RankedStudent;
  publicSample: boolean;
  demoLayout?: boolean;
}) {
  const [, setLocation] = useLocation();
  const trend = student.scoreTrend.map((item, index) => ({ index, score: item.score }));
  const rank = student.rank;
  const publicCode = encodeURIComponent(student.participantCode ?? String(student.studentId));
  const detailsPath = demoLayout
    ? `${DEMO_TEACHER_BASE}/students/${publicCode}`
    : publicSample
      ? `/demo/rankings/${publicCode}`
      : `/teacher/students/${student.studentId}`;
  const openDetails = () => setLocation(detailsPath);
  return (
    <tr
      role="link"
      tabIndex={0}
      onClick={openDetails}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openDetails();
        }
      }}
      aria-label={`${student.name}のスコアを見る`}
      className="cursor-pointer text-sm text-gray-700 hover:bg-cyan-50 focus:bg-cyan-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-cyan-700"
    >
      <td className="px-4 py-3">
        <span className={cn(
          'inline-grid h-7 min-w-7 place-items-center rounded-md px-1 font-bold',
          rank === 1 ? 'bg-amber-100 text-amber-900' : rank === 2 ? 'bg-gray-200 text-gray-800' : rank === 3 ? 'bg-orange-100 text-orange-900' : 'text-gray-700'
        )}>
          {rank}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="font-bold text-gray-900">{student.name}</div>
      </td>
      <td className="px-4 py-3 font-medium text-gray-700">{student.participantCode ?? '未登録'}</td>
      <td className="px-4 py-3 font-medium">{student.interviewCount}回</td>
      <td className="px-4 py-3 font-mono text-sm">{formatDuration(student.totalUsageSeconds)}</td>
      <td className="px-4 py-3">
        <span className={cn('inline-block rounded-md px-2.5 py-1 font-bold', scoreTone(student.averageScore))}>
          {student.averageScore.toFixed(1)}点
        </span>
      </td>
      <td className="h-14 px-4 py-2">
        {trend.length > 1 ? (
          <ResponsiveContainer width="100%" height={44}>
            <LineChart data={trend} margin={{ top: 5, right: 3, bottom: 5, left: 3 }}>
              <YAxis domain={[0, 100]} hide />
              <Line type="monotone" dataKey="score" stroke="#0e7490" strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-xs text-gray-400">履歴不足</div>
        )}
      </td>
      <td className="px-2 py-3">
        <span className="grid h-9 w-9 place-items-center rounded-md text-gray-500">
          <ChevronRight className="h-5 w-5" />
        </span>
      </td>
    </tr>
  );
}
